import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BriefcaseBusiness, Code2, Mail, MapPin, Phone, Send } from "lucide-react";
import { contactLinks } from "../data";
import { createMailtoLink, isContactEmailConfigured, sendContactMessage } from "../services/contactEmail";
import { trackEvent } from "../utils/analytics";

const icons = [Mail, Phone, MapPin, Code2, BriefcaseBusiness];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [isSending, setIsSending] = useState(false);
  const [notice, setNotice] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSending(true);
    setNotice(null);

    try {
      if (!isContactEmailConfigured()) {
        window.location.href = createMailtoLink(form);
        setNotice({ type: "info", message: "Email app нээгдлээ. EmailJS тохируулбал message шууд Gmail рүү очно." });
        return;
      }

      await sendContactMessage(form);
      trackEvent("contact_form_submit", { method: "emailjs" });
      setNotice({ type: "success", message: "Мессеж Gmail рүү амжилттай илгээгдлээ. Баярлалаа!" });
      setForm({ name: "", email: "", message: "" });
    } catch (error) {
      const message =
        error.message === "EMAIL_CONFIG_MISSING"
          ? "EmailJS тохиргоо дутуу байна. Доорх email/phone link-ээр холбогдоно уу."
          : "Мессеж илгээхэд алдаа гарлаа. Түр хүлээгээд дахин оролдоно уу.";
      setNotice({ type: "error", message });
    } finally {
      setIsSending(false);
      setTimeout(() => setNotice(null), 4200);
    }
  };

  return (
    <section id="contact" className="section-shell">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.6 }}
        className="mx-auto max-w-3xl text-center"
      >
        <p className="text-sm font-bold uppercase tracking-[0.35em] text-cyan-200">Холбоо барих</p>
        <h2 className="mt-4 font-display text-3xl font-black text-white sm:text-5xl">Холбоо барих</h2>
      </motion.div>

      <div className="mt-12 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="space-y-4"
        >
          {contactLinks.map((link, index) => {
            const Icon = icons[index];
            return (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel={link.href.startsWith("http") ? "noreferrer" : undefined}
                className="glass-card group flex items-center gap-3 p-4 transition duration-300 hover:-translate-y-1 hover:border-cyan-300/35 hover:shadow-glow sm:gap-4 sm:p-5"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-cyan-300/10 text-cyan-100 sm:h-12 sm:w-12">
                  <Icon size={22} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-black uppercase tracking-[0.24em] text-slate-500">{link.label}</span>
                  <span className="mt-1 block break-words text-sm font-semibold text-slate-100">{link.text}</span>
                </span>
              </a>
            );
          })}
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="glass-card p-5 sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-bold text-slate-300">Name</span>
              <input
                required
                name="name"
                value={form.name}
                onChange={handleChange}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300/50"
                placeholder="Таны нэр"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-slate-300">Email</span>
              <input
                required
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300/50"
                placeholder="your@email.com"
              />
            </label>
          </div>
          <label className="mt-5 block">
            <span className="text-sm font-bold text-slate-300">Message</span>
            <textarea
              required
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={6}
              className="mt-2 w-full resize-none rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300/50"
              placeholder="Мессежээ бичнэ үү"
            />
          </label>
          <button type="submit" disabled={isSending} className="btn-glow mt-6 w-full disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto">
            {isSending ? "Sending..." : "Send Message"} <Send size={18} />
          </button>
        </motion.form>
      </div>

      <AnimatePresence>
        {notice && (
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            className={`fixed inset-x-4 bottom-4 z-50 rounded-2xl border px-5 py-4 text-sm font-bold shadow-glow backdrop-blur-xl sm:left-auto sm:right-6 sm:w-auto ${
              notice.type === "error"
                ? "border-rose-300/30 bg-rose-950/90 text-rose-100"
                : "border-cyan-300/30 bg-slate-950/90 text-cyan-100"
            }`}
          >
            {notice.message}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
